// Admin manual create attendance.
// Spec: docs/admin-attendance-create-edit-history.md §3 (shifts list) + §4 (POST create)

import { useEffect, useMemo, useState } from 'react';
import { DatePicker, Form, Input, Modal, Select, notification } from 'antd';
import dayjs from 'dayjs';
import CustomButton from 'components/custom-button';
import DebounceSelect from 'components/debounce-select/select';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { AdminCreateAttendanceDto, Shift } from 'types/attendance';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';
import { mapAttendanceError } from '../helpers/attendance-error';
import {
  disabledAttendanceDate,
  isDateInWindow,
} from '../helpers/attendance-date-window';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface UserOption {
  value: number;
  label: string;
}

// Build display label: "FullName (username) — email" with sensible fallbacks.
function buildUserLabel(u: any): string {
  const full = [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
  const head =
    full && u?.username
      ? `${full} (${u.username})`
      : u?.nickName && u?.nickName !== u?.username
      ? `${u.nickName} (${u.username || ''})`
      : u?.username || u?.email || `User #${u?.id}`;
  return u?.email ? `${head} — ${u.email}` : head;
}

const fetchUsers = async (search: string): Promise<UserOption[]> => {
  // BE requires query >= 2 chars (after trim). Skip API call if shorter.
  const trimmed = search.trim();
  if (trimmed.length < 2) return [];
  const res: any = await AxiosUtils.get(
    `${API.searchUserAdmin}?query=${encodeURIComponent(trimmed)}`
  );
  const list: any[] = Array.isArray(res?.data) ? res.data : [];
  return list.map((u: any) => ({
    value: u.id,
    label: buildUserLabel(u),
  }));
};

function shiftLabel(s: Shift): string {
  return `${s.name} (${s.startTime}–${s.endTime})`;
}

const CreateAttendanceModal = ({ open, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);

  // Fetch shifts when the modal opens (per spec: cache locally, refresh on reopen acceptable).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetchShifts = async () => {
      setShiftsLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.attendanceShifts, {
          onlyActive: 'true',
        });
        const list: Shift[] = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) setShifts(list);
      } catch (err) {
        if (!cancelled) {
          notification.error({ message: mapAttendanceError(err) });
        }
      } finally {
        if (!cancelled) setShiftsLoading(false);
      }
    };
    fetchShifts();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Group shifts by department for nicer UX (per spec FE tips §3).
  const shiftOptions = useMemo(() => {
    const groups = new Map<string, Shift[]>();
    for (const s of shifts) {
      const key = s.department || 'Khác';
      const arr = groups.get(key) || [];
      arr.push(s);
      groups.set(key, arr);
    }
    return Array.from(groups.entries()).map(([dept, items]) => ({
      label: DEPARTMENT_LABEL[dept] || dept,
      options: items.map((s) => ({ label: shiftLabel(s), value: s.id })),
    }));
  }, [shifts]);

  // Form instance is owned by this component so `destroyOnClose` alone does
  // NOT clear values — must reset explicitly.
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: any) => {
    // DebounceSelect uses labelInValue → object; tolerate raw number too.
    const userIdRaw = values.userId;
    const userId =
      userIdRaw && typeof userIdRaw === 'object' ? userIdRaw.value : userIdRaw;
    if (!userId) {
      notification.warning({ message: 'Vui lòng chọn nhân viên.' });
      return;
    }

    if (!isDateInWindow(values.date)) {
      notification.warning({
        message: 'Ngày ngoài phạm vi cho phép (tháng hiện tại / tháng trước).',
      });
      return;
    }
    // Only enforce strict ordering when both times are provided.
    // Checkout is optional (user may have only checked in).
    if (
      values.checkoutTime &&
      !dayjs(values.checkoutTime).isAfter(dayjs(values.checkinTime))
    ) {
      notification.warning({ message: 'Giờ checkout phải sau giờ checkin.' });
      return;
    }

    const dto: AdminCreateAttendanceDto = {
      userId: Number(userId),
      shiftId: values.shiftId,
      date: dayjs(values.date).format('YYYY-MM-DD'),
      checkinTime: dayjs(values.checkinTime).toISOString(),
      reason: (values.reason || '').trim(),
    };
    if (values.checkoutTime) {
      dto.checkoutTime = dayjs(values.checkoutTime).toISOString();
    }

    setLoading(true);
    try {
      await AxiosUtils.post(API.attendanceAdmin, dto);
      notification.success({ message: 'Đã tạo bản ghi chấm công.' });
      onSuccess();
      handleClose();
    } catch (err) {
      notification.error({ message: mapAttendanceError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Tạo bản ghi chấm công"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="custom-form"
        initialValues={{ date: dayjs() }}
      >
        <Form.Item
          label="Nhân viên"
          name="userId"
          rules={[{ required: true, message: 'Vui lòng chọn nhân viên.' }]}
        >
          <DebounceSelect
            placeholder="Nhập tối thiểu 2 ký tự (username hoặc email)"
            fetchOptions={fetchUsers}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label="Ca làm"
          name="shiftId"
          rules={[{ required: true, message: 'Vui lòng chọn ca làm.' }]}
        >
          <Select
            placeholder="Chọn ca làm"
            loading={shiftsLoading}
            options={shiftOptions}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          label="Ngày"
          name="date"
          rules={[{ required: true, message: 'Vui lòng chọn ngày.' }]}
        >
          <DatePicker
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
            disabledDate={disabledAttendanceDate}
          />
        </Form.Item>
        <Form.Item
          label="Giờ checkin"
          name="checkinTime"
          rules={[{ required: true, message: 'Vui lòng nhập giờ checkin.' }]}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: '100%' }}
            placeholder="Chọn giờ checkin"
          />
        </Form.Item>
        <Form.Item
          label="Giờ checkout"
          name="checkoutTime"
          extra="Bỏ trống nếu user mới chỉ checkin."
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: '100%' }}
            placeholder="Chọn giờ checkout (tuỳ chọn)"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="Lý do"
          name="reason"
          rules={[
            { required: true, message: 'Vui lòng nhập lý do.' },
            { min: 5, message: 'Lý do tối thiểu 5 ký tự.' },
            { max: 500, message: 'Lý do tối đa 500 ký tự.' },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder="VD: User quên chấm công vì họp khẩn"
          />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text="Tạo"
            bgColor="secondary-green"
            htmlType="submit"
            loading={loading}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default CreateAttendanceModal;
